import { BudgetScenarioConsolidatedResult } from '@callmart/types';
import { QueryKey as TQueryKey, useMutation, useQuery } from '@tanstack/react-query';
import { QueryKey } from '../../enums/QueryKey';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import {
    exportBudgetScenarios,
    getScenarioConsolidated,
    type ScenarioConsolidatedParams,
} from '../../services/budgetScenarioConsolidated';
import { downloadBlobFile } from '../../utils/fileUtils';
import { ScenarioQueryState } from './budgetScenarios';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function useScenarioConsolidated(
    versionId: number | undefined,
    scenarioIds: number[],
    // CALL-794: whether `scenarioIds` is the selection the page MEANS, rather than the empty list it
    // stands at before one can be resolved. The version alone is not enough to enable the query: on a
    // shared ?scenarios= link the ids still have to be pruned against a candidate list that is loading,
    // and on a first visit there are no ids until the default is written — so the query fired with an
    // empty selection and fired again one render later, one of the two always wasted. That first
    // request is not a cheap probe: an empty selection still builds the whole version summary and
    // baseline, which is the expensive half of the response.
    isSelectionResolved: boolean
): [BudgetScenarioConsolidatedResult | undefined, boolean, ScenarioQueryState] {
    const enabled = versionId != null && isSelectionResolved;
    const { data, isFetching, isPreviousData, isError, error, refetch } = useQuery<
        BudgetScenarioConsolidatedResult,
        Error
    >({
        // The selection is part of the result's identity. React Query hashes the array by content, and
        // the page keeps the ids sorted, so the same set reached by a different route of clicks hits one
        // cache entry instead of refetching an identical board.
        queryKey: [QueryKey.BudgetScenarioConsolidated, versionId, scenarioIds] as TQueryKey,
        queryFn: () => getScenarioConsolidated({ versionId: versionId!, scenarioIds }),
        enabled,
        // Changing the selection must not blank the board. Every table on the page is derived from this
        // result, so dropping to `undefined` unmounts all of them and leaves the page empty below its
        // header for the seconds the request takes — which reads as broken, not busy. The board the user
        // was reading stays up, marked loading, until the new one lands.
        keepPreviousData: true,
    });

    return [
        data,
        // isFetching, not isLoading: with keepPreviousData a refetch keeps `data`, so `isLoading` stays
        // false throughout and the grids would give no sign that the numbers are being replaced.
        //
        // …and isPreviousData on top of it, because isFetching alone is false in the one state that
        // matters most: keepPreviousData does not check `enabled`, so a DISABLED new key (the selection
        // going unresolved while another bucket's candidates load) inherits the old key's board with
        // nothing in flight. Unflagged, that is the previous version's figures under the newly picked
        // budget's name. isPreviousData is exactly and only that condition — a same-key background
        // refetch keeps its own data and does not set it.
        isFetching || isPreviousData,
        {
            isError,
            error: error ?? null,
            refetch: () => {
                if (enabled) refetch();
            },
        },
    ];
}

// CALL-777: download the scenarios workbook for the selected version and scenario selection. Read-only,
// so nothing is invalidated on success.
//
// The file is named by the server (Content-Disposition, built from the version name); the id-based
// name below is only the fallback for a response without that header.
//
// The parameters still travel as the mutation VARIABLE, not via the closure: the version picker and
// scenario multiselect stay enabled while an export is in flight, and React Query refreshes a running
// mutation's options on re-render — so a closure-read id would let onSuccess see the newly selected
// version and save version A's workbook as budget-szenarien-vB.xlsx. The variable is fixed at
// invocation.
export function useExportBudgetScenarios() {
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<{ data: Blob; fileName?: string }, Error, ScenarioConsolidatedParams>({
        mutationFn: (params) => exportBudgetScenarios(params),
        onSuccess: ({ data, fileName }, { versionId }) => {
            downloadBlobFile({
                data,
                fileName: fileName || `budget-szenarien-v${versionId}.xlsx`,
                contentType: EXCEL_MIME,
            });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}
